import { useCallback, useEffect, useRef, useState } from 'react'
import { isNonNullable } from 'lib/array/arrayUtils'
import { useOptionalOfferPageAssistantSurfaceRegistryContext } from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantSurfaceRegistry'
import { NAVIGABLE_OFFER_CONTENT_SCROLL_OFFSET } from 'components/OfferPage/NavigableOfferContent/useNavigableOfferContentScroll'
import type { LargeContentToggleHandle } from 'components/Common/LargeContentToggle'

export interface ThingsToKnowRowFocusInput {
  key: string
  collapsable: boolean
}

/**
 * Registers a live focus target (`fine-print:<row.key>`) for each desktop Things-to-know row, so
 * `focus_accommodation_offer_page_content` can scroll to and highlight the real row -- matching
 * the row keys `offerPageFinePrintContext.ts`'s builders now tag onto the snapshot. Desktop only:
 * mobile rows render as an `Accordion`/static row, not `LargeContentToggle`, so there is nothing
 * here for them to expand.
 *
 * State-backed row-element refs (not a plain mutable `useRef`) -- matches `OfferPageFocusAnchor.tsx`'s
 * own pattern for the same problem, since mutating a plain ref's `.current` never triggers this
 * effect to re-run once a row's element actually mounts.
 */
export function useThingsToKnowRowFocusTargets(
  rows: Array<ThingsToKnowRowFocusInput>,
  scrollOffset: number = NAVIGABLE_OFFER_CONTENT_SCROLL_OFFSET,
): {
  getRowRef: (key: string) => (element: HTMLElement | null) => void
  getToggleRef: (key: string) => (handle: LargeContentToggleHandle | null) => void
} {
  const registry = useOptionalOfferPageAssistantSurfaceRegistryContext()
  const [rowElements, setRowElements] = useState<Record<string, HTMLElement | null>>({})
  const toggleHandles = useRef<Record<string, LargeContentToggleHandle | null>>({})

  // A fresh inline `ref={el => ...}` arrow is a new function every render, which makes React
  // detach and reattach the ref (calling it with `null`, then the element again) on every render
  // -- that round-trip through the `null` case flips the state-backed element back and forth
  // forever. Caching one stable callback per row key avoids the unnecessary detach/reattach.
  const rowRefCallbacks = useRef<Record<string, (element: HTMLElement | null) => void>>({})
  const getRowRef = useCallback((key: string) => {
    rowRefCallbacks.current[key] ??= (element: HTMLElement | null) => {
      setRowElements(prev => (prev[key] === element ? prev : { ...prev, [key]: element }))
    }
    return rowRefCallbacks.current[key]
  }, [])

  const toggleRefCallbacks = useRef<Record<string, (handle: LargeContentToggleHandle | null) => void>>({})
  const getToggleRef = useCallback((key: string) => {
    toggleRefCallbacks.current[key] ??= (handle: LargeContentToggleHandle | null) => {
      toggleHandles.current[key] = handle
    }
    return toggleRefCallbacks.current[key]
  }, [])

  useEffect(() => {
    if (!registry) {
      return undefined
    }

    const unregisters = rows.map((row) => {
      const element = rowElements[row.key]
      if (!element) {
        return undefined
      }

      return registry.registerFocusTarget(`fine-print:${row.key}`, {
        element,
        focusMode: 'expand_then_scroll',
        availability: 'rendered',
        // Clears the sticky "Location / Things to know / Reviews" sub-nav bar, matching the
        // offset that bar's own tab-click scroll already uses for this exact page (see
        // `useNavigableOfferContentScroll.ts`) -- without it the row's top lines land underneath it.
        getScrollOffset: () => scrollOffset,
        prepareSteps: row.collapsable ? [{
          kind: 'reveal_container',
          run: () => {
            toggleHandles.current[row.key]?.expand()
            return { status: 'completed' }
          },
        }] : undefined,
      })
    }).filter(isNonNullable)

    return () => unregisters.forEach(unregister => unregister())
  }, [registry, rows, rowElements, scrollOffset])

  return { getRowRef, getToggleRef }
}
